import { Box, Stack, Typography } from '@mui/material';
import { SxProps } from '@mui/system';
import { groupBy, isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as React from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { InfoCircleIcon } from '@/components/icons/InfoCircleIcon';
import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { Card } from '@/components/layout/Card/Card';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { MatcherSuggestion } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import {
  AISuggestionMatch,
  AISuggestionsMatcherAddNewAnythingModalProps,
  AISuggestionsMatcherVariant,
  SuggestionsGroup,
} from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.types';
import {
  addNewAnythingFromSuggestionName,
  getMatchFromNewId,
  getSuggestedActorDisplayName,
} from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.utils';
import { AISuggestionsMatcherGroup } from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcherGroup';
import { useAiSuggestionsMatcherContext } from '@/modules/aiSuggestions/AISuggestionsMatcher/context/aiSuggestionsMatcher.context';
import { AddNewAnythingModal } from '@/modules/common/AddNewAnythingModal/AddNewAnythingModal';
import { useDPFormRecipientTrackerContext } from '@/modules/dispositiveProvisions/contexts/DPFormRecipientTracker.context';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import {
  trustAdvisorLevelIndex,
  trusteeLevelIndex,
} from '@/modules/trustees/trustees.constants';
import { COLORS } from '@/styles/tokens/colors';
import { AiSuggestionKind } from '@/types/schema';

// Stable comparator that sorts by the result of a numeric key extractor.
// Equal keys preserve input order (Array.prototype.sort is stable in modern JS).
function byAscendingNumber<T>(getKey: (item: T) => number) {
  return (a: T, b: T) => getKey(a) - getKey(b);
}

interface AISuggestionsMatcherProps {
  variant: AISuggestionsMatcherVariant;
  suggestions: MatcherSuggestion[];
  topRightContent?: React.ReactNode;
  cardSx?: SxProps;
}

export function AISuggestionsMatcher({
  variant,
  suggestions,
  topRightContent,
  cardSx = {},
}: AISuggestionsMatcherProps) {
  const trackUserEvent = useTrackUserEvent();

  const { householdId } = useHouseholdDetailsContext();

  const {
    setMatcherVariant,
    matchedSuggestions,
    setMatchedSuggestions,
    handleCommitMatchedSuggestions,
    matcherRef,
    matcherConfigs: {
      nounPlural,
      suggestionKinds,
      addNewAnythingModalProps,
      showCommitButtonOnEmptySuggestions,
    },
    setNewlyAddedIDs,
  } = useAiSuggestionsMatcherContext();

  const { handleNewlyAddedRecipient } = useDPFormRecipientTrackerContext();

  const [addAnythingModalOpen, setAddAnythingModalOpen] = useState(false);
  const [addingForSuggestion, setAddingForSuggestion] =
    useState<MatcherSuggestion | null>(null);
  const [addAnythingModalPropsWithNames, setAddAnythingModalPropsWithNames] =
    useState<AISuggestionsMatcherAddNewAnythingModalProps>({
      addableTypes: [],
      addClientProfileModalProps: {},
      addClientOrganizationModalProps: {},
      addEntityModalProps: {},
    });

  useEffect(() => {
    setMatcherVariant(variant);
  }, [setMatcherVariant, variant]);

  // Sanity check to verify suggestions are supported by the current variant.
  const filteredSuggestions = useMemo(
    () => suggestions.filter((s) => suggestionKinds.includes(s.kind)),
    [suggestionKinds, suggestions]
  );

  const groupedSuggestions: SuggestionsGroup[] = useMemo(() => {
    switch (variant) {
      case AISuggestionsMatcherVariant.GRANTORS:
      case AISuggestionsMatcherVariant.SINGLE_GRANTOR:
      case AISuggestionsMatcherVariant.BENEFICIARIES_V2:
        return [
          {
            suggestions,
          },
        ];
      case AISuggestionsMatcherVariant.TRUSTEES: {
        const trusteesGroup: MatcherSuggestion[] = [];
        const successorTrusteesGroup: MatcherSuggestion[] = [];
        const trustAdvisorsGroup: MatcherSuggestion[] = [];

        suggestions.forEach((s) => {
          if (s.kind === AiSuggestionKind.Trustee) {
            trusteesGroup.push(s);
          } else if (s.kind === AiSuggestionKind.SuccessorTrustee) {
            successorTrusteesGroup.push(s);
          } else if (s.kind === AiSuggestionKind.TrustAdvisor) {
            trustAdvisorsGroup.push(s);
          }
        });

        return [
          {
            groupLabel: 'Trustees',
            suggestions: trusteesGroup,
          },
          {
            groupLabel: 'Successor trustees',
            suggestions: successorTrusteesGroup.sort(
              byAscendingNumber((s) =>
                trusteeLevelIndex(s.successorTrustee?.order)
              )
            ),
          },
          {
            groupLabel: 'Trust advisors',
            suggestions: trustAdvisorsGroup.sort(
              byAscendingNumber((s) =>
                trustAdvisorLevelIndex(s.trustAdvisor?.order)
              )
            ),
          },
        ];
      }
      case AISuggestionsMatcherVariant.SUBTRUST_TRUSTEES: {
        const trusteesGroup: MatcherSuggestion[] = [];
        const successorTrusteesGroup: MatcherSuggestion[] = [];
        const trustAdvisorsGroup: MatcherSuggestion[] = [];

        suggestions.forEach((s) => {
          if (s.kind === AiSuggestionKind.SubtrustTrustee) {
            trusteesGroup.push(s);
          } else if (s.kind === AiSuggestionKind.SubtrustSuccessorTrustee) {
            successorTrusteesGroup.push(s);
          } else if (s.kind === AiSuggestionKind.SubtrustTrustAdvisor) {
            trustAdvisorsGroup.push(s);
          }
        });

        return [
          {
            groupLabel: 'Trustees',
            suggestions: trusteesGroup,
          },
          {
            groupLabel: 'Successor trustees',
            suggestions: successorTrusteesGroup.sort(
              byAscendingNumber((s) =>
                trusteeLevelIndex(s.subtrustSuccessorTrustee?.order)
              )
            ),
          },
          {
            groupLabel: 'Trust advisors',
            suggestions: trustAdvisorsGroup.sort(
              byAscendingNumber((s) =>
                trustAdvisorLevelIndex(s.subtrustTrustAdvisor?.order)
              )
            ),
          },
        ];
      }
      case AISuggestionsMatcherVariant.BUSINESS_PEOPLE: {
        const ownersGroup: MatcherSuggestion[] = [];
        const keyPeopleGroup: MatcherSuggestion[] = [];

        suggestions.forEach((s) => {
          if (s.kind === AiSuggestionKind.BeneficialOwner) {
            ownersGroup.push(s);
          } else if (s.kind === AiSuggestionKind.KeyPerson) {
            keyPeopleGroup.push(s);
          }
        });

        return [
          {
            groupLabel: 'Beneficial owners',
            suggestions: ownersGroup,
          },
          {
            groupLabel: 'Key people',
            suggestions: keyPeopleGroup,
          },
        ];
      }
      case AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_1_DIES_FIRST:
      case AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_2_DIES_FIRST:
      case AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_1_DIES_FIRST_FIRST_DEATH:
      case AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_1_DIES_FIRST_SECOND_DEATH:
      case AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_2_DIES_FIRST_FIRST_DEATH:
      case AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_2_DIES_FIRST_SECOND_DEATH:
      case AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_1_DIES_FIRST:
      case AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_1_DIES_FIRST_FIRST_DEATH:
      case AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_1_DIES_FIRST_SECOND_DEATH:
      case AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_2_DIES_FIRST:
      case AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_2_DIES_FIRST_FIRST_DEATH:
      case AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_2_DIES_FIRST_SECOND_DEATH:
      case AISuggestionsMatcherVariant.SUBTRUST_LIFETIME_BENEFICIARIES:
      case AISuggestionsMatcherVariant.SUBTRUST_REMAINDER_BENEFICIARIES:
      case AISuggestionsMatcherVariant.BENEFICIARY_DESIGNATION_RECIPIENTS_PRIMARY:
      case AISuggestionsMatcherVariant.BENEFICIARY_DESIGNATION_RECIPIENTS_CONTINGENT: {
        return [
          {
            suggestions,
          },
        ];
      }
    }
  }, [suggestions, variant]);

  const handleOpenAddAnythingModal = useCallback(
    (suggestion: MatcherSuggestion) => {
      const {
        initialClientProfileName,
        initialClientOrganizationName,
        initialEntityName,
      } = addNewAnythingFromSuggestionName(suggestion);

      const suggestionKind = suggestion.kind;
      const props = addNewAnythingModalProps[suggestionKind];
      if (!props) {
        throw new Error(
          `Unknown addNewAnythingModalProps to use for suggestionKind: ${suggestionKind}`
        );
      }

      const copy = Object.assign({}, props);

      copy.addClientProfileModalProps.initialClientProfileName = {
        ...initialClientProfileName,
      };
      copy.addClientOrganizationModalProps.initialOrganizationName =
        initialClientOrganizationName;
      copy.addEntityModalProps.initialEntityName = initialEntityName;
      if (copy.addTestamentaryEntityModalProps) {
        copy.addTestamentaryEntityModalProps.defaultName = initialEntityName;
      }

      setAddAnythingModalPropsWithNames(copy);
      setAddingForSuggestion(suggestion);
      setAddAnythingModalOpen(true);
    },
    [addNewAnythingModalProps]
  );

  const handleAfterAddAnythingCreate = useCallback(
    (newId: string) => {
      if (!addingForSuggestion) {
        return;
      }

      const match = getMatchFromNewId(newId);

      if (!match) {
        throw new Error(
          `Unsupported selected suggestion match option: ${newId}`
        );
      }

      setMatchedSuggestions((prevState) => ({
        ...prevState,
        [addingForSuggestion.id]: { ...match, suggestion: addingForSuggestion },
      }));

      const displayName = getSuggestedActorDisplayName(addingForSuggestion);
      if ('testamentaryId' in match) {
        handleNewlyAddedRecipient({
          id: match.testamentaryId,
          displayName,
        });
      }

      setNewlyAddedIDs((prevState) => [...prevState, newId]);

      setAddingForSuggestion(null);
      setAddAnythingModalOpen(false);
    },
    [
      addingForSuggestion,
      setMatchedSuggestions,
      setNewlyAddedIDs,
      handleNewlyAddedRecipient,
    ]
  );

  const handleConfirmAll = useCallback(() => {
    const areAnySuggestionsMatched = filteredSuggestions.some(
      (s) => matchedSuggestions[s.id]
    );

    // If no suggestions are matched, focus on the first select input
    if (!areAnySuggestionsMatched && filteredSuggestions.length > 0) {
      // Find the first select input within the matcher component
      const firstSelectInput = matcherRef.current?.querySelector(
        '[data-testid="ai-suggestions-matcher-row"] [data-testid^="select-"]'
      ) as HTMLElement;

      if (firstSelectInput) {
        // Focus the select input first
        firstSelectInput.focus();

        // Trigger mousedown event to open the dropdown
        const mouseDownEvent = new MouseEvent('mousedown', {
          bubbles: true,
          cancelable: true,
        });
        firstSelectInput.dispatchEvent(mouseDownEvent);

        return; // Don't proceed with committing empty suggestions
      }
    }

    handleCommitMatchedSuggestions(matchedSuggestions);

    // Group the suggestions by kind and track the user event for each kind.
    const groupedByKind = groupBy(
      Object.values(matchedSuggestions),
      (s) => s.suggestion.kind
    );
    Object.entries(groupedByKind).forEach(([kind, suggestions]) => {
      trackUserEvent('ai_suggestion accept', {
        suggestionKind: kind,
        count: suggestions.length,
      });
    });
  }, [
    filteredSuggestions,
    handleCommitMatchedSuggestions,
    matchedSuggestions,
    trackUserEvent,
    matcherRef,
  ]);

  const handleIgnoreAll = useCallback(() => {
    // Ignoring the suggestions will acknowledge them as "REJECTED" and not
    // show them in the UI again. This will NOT be tracked as a user event.
    const ignoredSuggestions = filteredSuggestions.reduce<
      Record<string, AISuggestionMatch>
    >((acc, s) => {
      acc[s.id] = {
        suggestion: s,
      };
      return acc;
    }, {});
    handleCommitMatchedSuggestions(ignoredSuggestions);
  }, [filteredSuggestions, handleCommitMatchedSuggestions]);

  if (
    !householdId ||
    (!showCommitButtonOnEmptySuggestions && isEmpty(filteredSuggestions))
  ) {
    return null;
  }

  return (
    <>
      <Card
        variant="filled-teal"
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          overflow: 'visible',
          border: `1px solid ${COLORS.TEAL[200]}`,
          ...cardSx,
        }}
        data-testid="ai-suggestions-matcher"
        inputRef={matcherRef}
      >
        {!isEmpty(filteredSuggestions) && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <Stars01Icon size={16} color={COLORS.NAVY[700]} />
              <Typography variant="title-s" color={COLORS.TEXT.PRIMARY}>
                Confirm {nounPlural}
              </Typography>
              <Tooltip title="Map suggested recipients from document to an individual in Luminary.">
                <InfoCircleIcon
                  size={16}
                  color={COLORS.NAVY[700]}
                  sx={{ display: 'flex', cursor: 'pointer' }}
                />
              </Tooltip>
            </Stack>
            {topRightContent}
          </Stack>
        )}
        <Box>
          {groupedSuggestions.map((sg) => (
            <AISuggestionsMatcherGroup
              key={sg.groupLabel}
              variant={variant}
              suggestionsGroup={sg}
              onSetSuggestionMatch={(suggestionId, matched) => {
                setMatchedSuggestions((prevState) => ({
                  ...prevState,
                  [suggestionId]: matched,
                }));
              }}
              onOpenAddAnythingModal={handleOpenAddAnythingModal}
            />
          ))}
        </Box>
        <Stack
          gap={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body-xs">
            Luminary AI can make mistakes. Please check for accuracy.
          </Typography>
          {filteredSuggestions.length > 1 && (
            <Stack direction="row" gap={1}>
              <Button
                variant="primary"
                size="xs"
                onClick={handleConfirmAll}
                data-testid="button-confirmAllSuggestions"
              >
                Confirm all
              </Button>
              <Button
                variant="transparent"
                size="xs"
                onClick={handleIgnoreAll}
                data-testid="button-ignoreAllSuggestions"
              >
                Ignore all
              </Button>
            </Stack>
          )}
        </Stack>
      </Card>
      <AddNewAnythingModal
        handleClose={() => setAddAnythingModalOpen(false)}
        onAfterCreate={handleAfterAddAnythingCreate}
        isOpen={addAnythingModalOpen}
        householdId={householdId}
        {...addAnythingModalPropsWithNames}
      />
    </>
  );
}
